import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faEye,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportPhysicianBatchProcessedRows,
  getPhysicianBatchProcessedRows,
  getPhysicianBatchUploadHistory,
} from "../../services/physicianBatchUploadApi";

const PhysicianBatchUploadProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();

  const [uploadInfo, setUploadInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  const [filterProviderCode, setFilterProviderCode] = useState("");
  const [filterLastName, setFilterLastName] = useState("");
  const [filterFirstName, setFilterFirstName] = useState("");
  const [filterTin, setFilterTin] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDetails, setFilterDetails] = useState("");

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await getPhysicianBatchUploadHistory({
        page: 1,
        limit: 20,
      });
      setHistory(response.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch upload history.");
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchUploadInfo = useCallback(async () => {
    if (!uploadId) return;
    try {
      const response = await getPhysicianBatchUploadHistory({
        page: 1,
        limit: 1000,
      });
      const upload = (response.data.data || []).find((item) => item.upload_id === uploadId);
      if (!upload) {
        toast.error("Upload not found.");
        navigate("/history-physician-batch-upload");
        return;
      }
      setUploadInfo(upload);
    } catch (error) {
      toast.error("Failed to fetch upload details.");
      console.error(error);
    }
  }, [uploadId, navigate]);

  const fetchRows = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const response = await getPhysicianBatchProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      const data = response.data.data || [];
      setAllRows(data);
      setRows(data);
      setPage(1);
    } catch (error) {
      toast.error("Failed to fetch processed rows.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    if (uploadId) {
      fetchUploadInfo();
      fetchRows();
      return;
    }
    fetchHistory();
  }, [uploadId, fetchUploadInfo, fetchRows, fetchHistory]);

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      const contains = (value, filterValue) =>
        !filterValue ||
        String(value ?? "")
          .toLowerCase()
          .includes(filterValue.toLowerCase());

      if (!contains(row.provider_code, filterProviderCode)) return false;
      if (!contains(row.last_name, filterLastName)) return false;
      if (!contains(row.first_name, filterFirstName)) return false;
      if (!contains(row.tin_no, filterTin)) return false;
      if (!contains(row.result_type, filterStatus)) return false;
      const detailsText = row.error_message || row.processed_by || "";
      if (!contains(detailsText, filterDetails)) return false;
      return true;
    });
  }, [
    allRows,
    filterProviderCode,
    filterLastName,
    filterFirstName,
    filterTin,
    filterStatus,
    filterDetails,
  ]);

  useEffect(() => {
    setRows(filteredRows);
  }, [filteredRows]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return rows.slice(start, start + perPage);
  }, [rows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));
  const hasActiveFilters =
    filterProviderCode || filterLastName || filterFirstName || filterTin || filterStatus || filterDetails;

  const handleClearFilters = () => {
    setFilterProviderCode("");
    setFilterLastName("");
    setFilterFirstName("");
    setFilterTin("");
    setFilterStatus("");
    setFilterDetails("");
    setPage(1);
  };

  const handleExport = async (type) => {
    if (!uploadId) return;
    setExporting(true);
    try {
      const response = await exportPhysicianBatchProcessedRows(uploadId, { type });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `physician_batch_upload_${uploadId}_${type}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${type} rows successfully.`);
    } catch (error) {
      toast.error("Export failed.");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const getResultBadge = (resultType) => {
    if (resultType === "success") {
      return (
        <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">
          Success
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">
        Failed
      </span>
    );
  };

  if (!uploadId) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-full mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <button
              onClick={() => navigate("/history-physician-batch-upload")}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Physician Batch Upload: View Processed Data</h1>
              <p className="text-gray-600 text-sm mt-0.5">
                Select an upload from history to view processed rows.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">Recent Uploads</h2>
              <button
                onClick={fetchHistory}
                disabled={historyLoading}
                className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faRefresh} className={historyLoading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">File Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Uploaded By</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Rows</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Uploaded</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No uploads found.
                      </td>
                    </tr>
                  ) : (
                    history.map((item) => (
                      <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-800">{item.original_file_name}</p>
                          <p className="text-xs text-gray-400 font-mono">{item.upload_id}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{item.uploaded_by_name || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 capitalize">{item.status || "uploaded"}</td>
                        <td className="px-4 py-3 text-center text-sm text-gray-700">{item.total_rows || 0}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{formatDateTime(item.created_at)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => navigate(`/processed-physician-batch-upload/${item.upload_id}`)}
                            disabled={item.status === "uploaded" || item.status === "processing"}
                            className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded text-xs font-medium flex items-center gap-1.5 mx-auto"
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    );
  }

  if (!uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading upload information...</p>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/history-physician-batch-upload")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Physician Batch Upload: Processed Data</h1>
            <p className="text-gray-600 text-sm mt-0.5">{uploadInfo.original_file_name}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Success</p>
              <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Failed</p>
              <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p
                className={`text-lg font-bold ${
                  uploadInfo.status === "completed"
                    ? "text-green-600"
                    : uploadInfo.status === "failed"
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                {(uploadInfo.status || "").charAt(0).toUpperCase() + (uploadInfo.status || "").slice(1)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                {uploadInfo.uploaded_by_name || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded</p>
              <p className="text-xs text-gray-700">{formatDateTime(uploadInfo.created_at)}</p>
            </div>
          </div>

          {uploadInfo.error_message && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {uploadInfo.error_message}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {[
                {
                  key: "all",
                  label: `All Rows (${uploadInfo.total_rows || 0})`,
                  activeClass: "bg-blue-600 text-white",
                },
                {
                  key: "success",
                  label: `Success (${uploadInfo.success_count || 0})`,
                  activeClass: "bg-green-600 text-white",
                },
                {
                  key: "failed",
                  label: `Failed (${uploadInfo.failed_count || 0})`,
                  activeClass: "bg-red-600 text-white",
                },
              ].map(({ key, label, activeClass }) => (
                <button
                  key={key}
                  onClick={() => {
                    setRowType(key);
                    setPage(1);
                  }}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    rowType === key ? activeClass : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
              <button
                onClick={fetchRows}
                disabled={loading}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleExport("all")}
                disabled={exporting || (uploadInfo.total_rows || 0) === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} />
                Export All
              </button>
              {rowType === "failed" && (
                <button
                  onClick={() => handleExport("failed")}
                  disabled={exporting || (uploadInfo.failed_count || 0) === 0}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Export Failed
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
              Filter Rows
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faTimes} />
                Clear Filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Provider Code</label>
              <input
                type="text"
                value={filterProviderCode}
                onChange={(event) => {
                  setFilterProviderCode(event.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Last Name</label>
              <input
                type="text"
                value={filterLastName}
                onChange={(event) => {
                  setFilterLastName(event.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">First Name</label>
              <input
                type="text"
                value={filterFirstName}
                onChange={(event) => {
                  setFilterFirstName(event.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">TIN</label>
              <input
                type="text"
                value={filterTin}
                onChange={(event) => {
                  setFilterTin(event.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(event) => {
                  setFilterStatus(event.target.value);
                  setPage(1);
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Details</label>
              <input
                type="text"
                value={filterDetails}
                onChange={(event) => {
                  setFilterDetails(event.target.value);
                  setPage(1);
                }}
                placeholder="Error or processor..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-xs text-blue-600 mt-2">
              Showing {rows.length} of {allRows.length} rows (filtered)
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Row #</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Provider Code</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Last Name</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">First Name</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Gender</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Specialization</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">TIN</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">PRC#</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Physician ID</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-3 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-3 py-8 text-center text-gray-500">
                      No rows found.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, index) => (
                    <tr key={`${row.id}-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-700 font-medium">{row.row_number}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.provider_code || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.last_name || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.first_name || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.gender || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.specialization || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.tin_no || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.prc_no || "-"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.physician_id || "-"}</td>
                      <td className="px-3 py-2.5">{getResultBadge(row.result_type)}</td>
                      <td className="px-3 py-2.5 text-xs max-w-xs">
                        {row.error_message ? (
                          <div className="text-red-700 truncate" title={row.error_message}>
                            {row.error_message}
                          </div>
                        ) : (
                          <span className="text-gray-500">Processed by {row.processed_by || "-"}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {rows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, rows.length)} of {rows.length} rows
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PhysicianBatchUploadProcessedPage;
